import React from 'react';
import {
  Alert, Checkbox, Select, TextInput, Textarea, useToast,
} from '@parsina/framework';

import { useTranslation, toAsciiDigits } from '../../i18n';
import TaskDialog from '../../components/TaskDialog';
import {
  CONTACT_CHANNELS, CONTACT_MADE_OUTCOMES, INTERACTION_OUTCOMES, NOTE_MAX,
  channelKey, leadStatusKey, outcomeKey,
} from '../../constants/leadPipeline';
import { crmErrorMessage, crmGql } from '../../lib/crmApi';
import { LOG_INTERACTION, SET_LEAD_STATUS } from './queries';

/** Minutes, as typed in any digits; '' = not given. Refuses anything but a whole number 0–600. */
export function parseMinutes(raw) {
  const text = toAsciiDigits(String(raw ?? '')).trim();
  if (!text) return { value: null, invalid: false };
  if (!/^\d{1,3}$/.test(text)) return { value: null, invalid: true };
  const n = Number(text);
  return n > 600 ? { value: null, invalid: true } : { value: n, invalid: false };
}

/**
 * «ثبت تماس» — record one contact attempt (§3.2 T4).
 *
 * Writes `crmLogInteraction(customerId, { leadId, direction: 'outbound', … })`;
 * the actor is the token's `sub`, stamped by the CRM. When the lead is still
 * `new` and the outcome means a real conversation happened (answered /
 * interested / callback requested / meeting set), the dialog offers —
 * pre-checked — to move it to «تماس گرفته‌شده» in the same submit. A no-answer
 * or busy line never offers it: that is not a contact, and advancing on it
 * would silently stop the «unanswered» clock.
 *
 * The two writes are separate calls. If the interaction lands and the status
 * move is refused, the dialog closes (resubmitting would log the call twice)
 * and the refusal is reported as a toast with the server's own wording.
 */
export default function LogContactDialog({
  open, onOpenChange, lead, onDone,
}) {
  const { t } = useTranslation();
  const toast = useToast();

  const [channel, setChannel] = React.useState('call');
  const [outcome, setOutcome] = React.useState('');
  const [minutes, setMinutes] = React.useState('');
  const [note, setNote] = React.useState('');
  const [advance, setAdvance] = React.useState(true);
  const [touched, setTouched] = React.useState(false);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!open) return;
    setChannel('call'); setOutcome(''); setMinutes(''); setNote('');
    setAdvance(true); setTouched(false); setError(null);
  }, [open]);

  const canAdvance = lead?.status === 'new'
    && (lead?.allowedNextStatuses || []).includes('contacted')
    && CONTACT_MADE_OUTCOMES.includes(outcome);

  const duration = parseMinutes(minutes);
  const outcomeError = touched && !outcome ? t('demoRequests.logContact.outcomeRequired') : undefined;
  const dirty = !!(outcome || minutes || note || channel !== 'call');

  const submit = async () => {
    setTouched(true);
    if (!outcome || duration.invalid || pending) return;
    setPending(true);
    setError(null);
    let logged = false;
    try {
      await crmGql(LOG_INTERACTION, {
        customerId: lead.customerId,
        input: {
          leadId: lead.id,
          channel,
          direction: 'outbound',
          outcome,
          body: note.trim() || null,
          durationSeconds: duration.value == null ? null : duration.value * 60,
        },
      });
      logged = true;
      if (canAdvance && advance) {
        await crmGql(SET_LEAD_STATUS, { leadId: lead.id, status: 'contacted', lostReason: null });
        toast.success(t('demoRequests.logContact.successAdvanced'));
      } else {
        toast.success(t('demoRequests.logContact.success'));
      }
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      if (logged) {
        // The call IS recorded; keeping the dialog open would invite a second copy.
        toast.error(t('demoRequests.logContact.advanceFailed', { message: crmErrorMessage(e, t) }));
        onOpenChange(false);
        onDone?.();
      } else {
        setError(crmErrorMessage(e, t));
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <TaskDialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('demoRequests.logContact.title')}
      error={error}
      pending={pending}
      dirty={dirty}
      submitLabel={t('demoRequests.logContact.submit')}
      submitDisabled={duration.invalid}
      onSubmit={submit}
      data-testid="demo-log-dialog"
    >
      <div className="grid grid-cols-2 gap-3">
        <Select
          label={t('demoRequests.logContact.channel')}
          value={channel}
          onChange={(e) => setChannel(e.target?.value ?? e)}
          options={CONTACT_CHANNELS.map((c) => ({ value: c, label: t(channelKey(c)) }))}
          placeholder=""
          disabled={pending}
          data-testid="demo-log-channel"
        />
        <Select
          label={t('demoRequests.logContact.outcome')}
          value={outcome}
          onChange={(e) => { setOutcome(e.target?.value ?? e); setTouched(true); }}
          onBlur={() => setTouched(true)}
          options={INTERACTION_OUTCOMES.map((o) => ({ value: o, label: t(outcomeKey(o)) }))}
          placeholder={t('demoRequests.logContact.outcomePlaceholder')}
          error={outcomeError}
          disabled={pending}
          data-testid="demo-log-outcome"
        />
      </div>

      <TextInput
        label={t('demoRequests.logContact.minutes')}
        value={minutes}
        onChange={(e) => setMinutes(e.target?.value ?? e)}
        inputMode="numeric"
        dir="ltr"
        maxLength={3}
        helperText={t('demoRequests.logContact.minutesHelp')}
        error={duration.invalid ? t('demoRequests.logContact.minutesInvalid') : undefined}
        disabled={pending}
        style={{ maxWidth: '12rem' }}
        data-testid="demo-log-minutes"
      />

      <Textarea
        label={t('demoRequests.logContact.note')}
        value={note}
        onChange={(e) => setNote(e.target?.value ?? e)}
        maxLength={NOTE_MAX}
        showCount
        rows={3}
        disabled={pending}
        data-testid="demo-log-note"
      />

      {canAdvance ? (
        <Alert variant="info" data-testid="demo-log-advance">
          <Checkbox
            checked={advance}
            onChange={(v) => setAdvance(v === true)}
            disabled={pending}
            label={t('demoRequests.logContact.advance', { status: t(leadStatusKey('contacted')) })}
            data-testid="demo-log-advance-checkbox"
          />
        </Alert>
      ) : lead?.status === 'new' && outcome && !CONTACT_MADE_OUTCOMES.includes(outcome) ? (
        <p className="text-xs text-muted-foreground" data-testid="demo-log-no-advance">
          {t('demoRequests.logContact.noAdvance')}
        </p>
      ) : null}
    </TaskDialog>
  );
}
